import { useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  InputField,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@jem-hr/jem-ui";
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  MapPin,
  MoreVertical,
  Plus,
  X,
} from "lucide-react";

/* ============================================================
 * Mock data — 2 Regions, 4 Sites, with Roster summaries.
 * ============================================================ */

interface OverviewRoster {
  id: string;
  name: string;
  shiftLabel: string;
  assigned: number;
}

interface OverviewSite {
  id: string;
  name: string;
  addressPreview: string;
  rosters: OverviewRoster[];
}

interface OverviewRegion {
  id: string;
  name: string;
  sites: OverviewSite[];
}

const INITIAL_REGIONS: OverviewRegion[] = [
  {
    id: "region-gauteng",
    name: "Gauteng",
    sites: [
      {
        id: "site-newtown",
        name: "Newtown Site",
        addressPreview: "12 Henry Nxumalo Street, Newtown, Johannesburg",
        rosters: [
          {
            id: "roster-day",
            name: "Main Building",
            shiftLabel: "Day · Night",
            assigned: 50,
          },
          {
            id: "roster-warehouse",
            name: "Warehouse Shift",
            shiftLabel: "07:00–15:00",
            assigned: 22,
          },
          {
            id: "roster-loading-dock",
            name: "Loading Dock",
            shiftLabel: "08:00–17:00",
            assigned: 9,
          },
        ],
      },
      {
        id: "site-sandton",
        name: "Sandton Site",
        addressPreview: "45 Rivonia Road, Sandton, Johannesburg",
        rosters: [
          {
            id: "sandton-day",
            name: "Day Shift",
            shiftLabel: "06:00–14:00",
            assigned: 24,
          },
          {
            id: "sandton-night",
            name: "Night Shift",
            shiftLabel: "22:00–06:00",
            assigned: 8,
          },
        ],
      },
    ],
  },
  {
    id: "region-western-cape",
    name: "Western Cape",
    sites: [
      {
        id: "site-cape-town",
        name: "Cape Town Office",
        addressPreview: "5 Bree Street, Cape Town",
        rosters: [
          {
            id: "ct-office",
            name: "Office Shift",
            shiftLabel: "08:00–17:00",
            assigned: 16,
          },
        ],
      },
    ],
  },
];

export interface SitesAndRegionsProps {
  onNavigateToSite?: (siteId: string) => void;
}

export function SitesAndRegions({ onNavigateToSite }: SitesAndRegionsProps = {}) {
  const [regions, setRegions] = useState<OverviewRegion[]>(INITIAL_REGIONS);

  /* -------------- Create Region -------------- */
  const [createRegionOpen, setCreateRegionOpen] = useState(false);
  const [newRegionName, setNewRegionName] = useState("");

  /* -------------- Create Site -------------- */
  const [createSiteOpen, setCreateSiteOpen] = useState(false);
  const [newSiteRegionId, setNewSiteRegionId] = useState<string>("");
  const [newSiteName, setNewSiteName] = useState("");
  const [newSiteAddress, setNewSiteAddress] = useState("");

  function openCreateSite(regionId?: string) {
    setNewSiteRegionId(regionId ?? "");
    setNewSiteName("");
    setNewSiteAddress("");
    setCreateSiteOpen(true);
  }

  function closeCreateSite() {
    setCreateSiteOpen(false);
    setNewSiteRegionId("");
    setNewSiteName("");
    setNewSiteAddress("");
  }

  /* -------------- Move Site -------------- */
  const [pendingMoveSite, setPendingMoveSite] = useState<{
    siteId: string;
    fromRegionId: string;
  } | null>(null);
  const [moveSiteDestination, setMoveSiteDestination] = useState<string>("");

  /* -------------- Move Roster -------------- */
  const [pendingMoveRoster, setPendingMoveRoster] = useState<{
    rosterId: string;
    fromSiteId: string;
  } | null>(null);
  const [moveRosterDestination, setMoveRosterDestination] = useState<string>("");

  /* -------------- Success Alert -------------- */
  const [successAlert, setSuccessAlert] = useState<{
    title: string;
    body: string;
  } | null>(null);

  /* -------------- Derived: total roster count per region -------------- */
  function regionRosterCount(region: OverviewRegion): number {
    return region.sites.reduce((acc, s) => acc + s.rosters.length, 0);
  }

  /* -------------- Handlers -------------- */
  function confirmCreateRegion() {
    const name = newRegionName.trim();
    if (!name) return;
    const id = `region-${Date.now()}`;
    setRegions((rs) => [...rs, { id, name, sites: [] }]);
    setSuccessAlert({ title: "Region created", body: `${name} added.` });
    setCreateRegionOpen(false);
    setNewRegionName("");
  }

  function confirmCreateSite() {
    const regionId = newSiteRegionId;
    const name = newSiteName.trim();
    if (!regionId || !name) return;
    const id = `site-${Date.now()}`;
    setRegions((rs) =>
      rs.map((r) =>
        r.id === regionId
          ? {
              ...r,
              sites: [
                ...r.sites,
                {
                  id,
                  name,
                  addressPreview: newSiteAddress.trim() || "Address not set",
                  rosters: [],
                },
              ],
            }
          : r
      )
    );
    const regionName = regions.find((r) => r.id === regionId)?.name ?? "region";
    setSuccessAlert({
      title: "Site created",
      body: `${name} added to ${regionName}.`,
    });
    closeCreateSite();
  }

  function confirmMoveSite() {
    if (!pendingMoveSite || !moveSiteDestination) return;
    const { siteId, fromRegionId } = pendingMoveSite;
    const fromRegion = regions.find((r) => r.id === fromRegionId);
    const site = fromRegion?.sites.find((s) => s.id === siteId);
    const toRegion = regions.find((r) => r.id === moveSiteDestination);
    if (!site || !toRegion) return;
    setRegions((rs) =>
      rs.map((r) => {
        if (r.id === fromRegionId) {
          return { ...r, sites: r.sites.filter((s) => s.id !== siteId) };
        }
        if (r.id === moveSiteDestination) {
          return { ...r, sites: [...r.sites, site] };
        }
        return r;
      })
    );
    setSuccessAlert({
      title: "Site moved",
      body: `${site.name} moved to ${toRegion.name}.`,
    });
    setPendingMoveSite(null);
    setMoveSiteDestination("");
  }

  function confirmMoveRoster() {
    if (!pendingMoveRoster || !moveRosterDestination) return;
    const { rosterId, fromSiteId } = pendingMoveRoster;
    let movedRoster: OverviewRoster | null = null;
    let destSiteName = "";
    setRegions((rs) =>
      rs.map((r) => ({
        ...r,
        sites: r.sites.map((s) => {
          if (s.id === fromSiteId) {
            const found = s.rosters.find((rr) => rr.id === rosterId);
            if (found) movedRoster = found;
            return {
              ...s,
              rosters: s.rosters.filter((rr) => rr.id !== rosterId),
            };
          }
          if (s.id === moveRosterDestination) {
            destSiteName = s.name;
            return s;
          }
          return s;
        }),
      }))
    );
    if (movedRoster) {
      const rosterToAdd: OverviewRoster = movedRoster;
      setRegions((rs) =>
        rs.map((r) => ({
          ...r,
          sites: r.sites.map((s) =>
            s.id === moveRosterDestination
              ? { ...s, rosters: [...s.rosters, rosterToAdd] }
              : s
          ),
        }))
      );
      setSuccessAlert({
        title: "Roster moved",
        body: `${rosterToAdd.name} moved to ${destSiteName}. All settings reset to the destination Site's defaults.`,
      });
    }
    setPendingMoveRoster(null);
    setMoveRosterDestination("");
  }

  // Flat list of all sites (across regions) for the Move Roster destination select.
  const allSites = regions.flatMap((r) =>
    r.sites.map((s) => ({
      id: s.id,
      label: `${s.name} (${r.name})`,
    }))
  );

  /* -------------- Render -------------- */
  return (
    <>
      <div
        className="-mx-xl -mt-lg px-xl py-lg mb-md"
        style={{ backgroundColor: "rgba(237,244,251,0.5)" }}
      >
        <h1 className="font-heading text-2xl font-semibold leading-36 text-greyscale-text-title">
          Sites &amp; Regions
        </h1>
        <p className="mt-xxs text-sm text-greyscale-text-body max-w-[760px]">
          Browse the hierarchy of Regions, Sites, and Rosters. Create new
          Regions and Sites, and move Sites between Regions.
        </p>
      </div>

      <div className="space-y-md max-w-[1100px] pb-[120px]">
        {/* Top action bar */}
        <div className="flex flex-wrap items-center justify-between gap-sm">
          <p className="text-sm text-greyscale-text-body">
            <strong>{regions.length}</strong> Region{regions.length === 1 ? "" : "s"}
            <span className="mx-xs text-greyscale-text-caption">·</span>
            <strong>
              {regions.reduce((acc, r) => acc + r.sites.length, 0)}
            </strong>{" "}
            Sites
            <span className="mx-xs text-greyscale-text-caption">·</span>
            <strong>
              {regions.reduce((acc, r) => acc + regionRosterCount(r), 0)}
            </strong>{" "}
            Rosters
          </p>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="default"
                size="sm"
                leftIcon={<Plus size={14} />}
                rightIcon={<ChevronDown size={14} />}
              >
                Create
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setCreateRegionOpen(true)}>
                New Region
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => openCreateSite()}>
                New Site
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Success Alert */}
        {successAlert && (
          <Alert variant="success" className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-xs top-xs h-6 w-6"
              aria-label="Dismiss"
              onClick={() => setSuccessAlert(null)}
            >
              <X size={14} />
            </Button>
            <AlertTitle icon={<CheckCircle2 size={16} />}>
              {successAlert.title}
            </AlertTitle>
            <AlertDescription>
              <p className="pr-lg">{successAlert.body}</p>
            </AlertDescription>
          </Alert>
        )}

        {/* Regions accordion */}
        <Accordion
          type="multiple"
          defaultValue={regions.map((r) => r.id)}
          className="space-y-md"
        >
          {regions.map((region) => {
            const totalRosters = regionRosterCount(region);
            return (
              <AccordionItem
                key={region.id}
                value={region.id}
                className="overflow-hidden rounded-lg border border-greyscale-border bg-white last:border-b"
              >
                <AccordionTrigger className="!px-10 py-md hover:no-underline">
                  <div className="text-left">
                    <h2 className="font-heading text-lg font-semibold text-greyscale-text-title">
                      {region.name}
                    </h2>
                    <p className="text-xs text-greyscale-text-caption">
                      <strong className="text-greyscale-text-title">
                        {region.sites.length}
                      </strong>{" "}
                      Site{region.sites.length === 1 ? "" : "s"}
                      <span className="mx-xxs">·</span>
                      <strong className="text-greyscale-text-title">
                        {totalRosters}
                      </strong>{" "}
                      Roster{totalRosters === 1 ? "" : "s"}
                    </p>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="border-t border-greyscale-border">
                    {region.sites.length === 0 ? (
                      <div className="flex flex-col items-center justify-center gap-xs p-xl text-center">
                        <p className="text-sm text-greyscale-text-caption">
                          No Sites in this Region yet.
                        </p>
                        <Button
                          variant="default"
                          size="sm"
                          leftIcon={<Plus size={14} />}
                          onClick={() => openCreateSite(region.id)}
                        >
                          Add the first Site
                        </Button>
                      </div>
                    ) : (
                      <ul className="divide-y divide-greyscale-border">
                        {region.sites.map((site) => (
                          <li
                            key={site.id}
                            className="flex flex-wrap items-start justify-between gap-md p-md"
                          >
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-greyscale-text-title">
                                {site.name}
                              </p>
                              <p className="mt-xxxs flex items-center gap-xxs text-xs text-greyscale-text-caption">
                                <MapPin size={12} aria-hidden="true" />
                                {site.addressPreview}
                              </p>
                              {site.rosters.length > 0 && (
                                <ul className="mt-xs space-y-xxs">
                                  {site.rosters.map((roster) => (
                                    <li
                                      key={roster.id}
                                      className="flex flex-wrap items-center justify-between gap-xs"
                                    >
                                      <span className="text-xs text-greyscale-text-body">
                                        <strong>{roster.name}</strong>{" "}
                                        <span className="text-greyscale-text-caption">
                                          ({roster.shiftLabel} ·{" "}
                                          {roster.assigned} assigned)
                                        </span>
                                      </span>
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            aria-label={`Actions for ${roster.name}`}
                                            onClick={(e) =>
                                              e.stopPropagation()
                                            }
                                          >
                                            <MoreVertical size={14} />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent
                                          align="end"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <DropdownMenuItem
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setPendingMoveRoster({
                                                rosterId: roster.id,
                                                fromSiteId: site.id,
                                              });
                                            }}
                                          >
                                            Move to Site…
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </li>
                                  ))}
                                </ul>
                              )}
                              {site.rosters.length === 0 && (
                                <p className="mt-xs text-xs text-greyscale-text-caption">
                                  No Rosters on this Site yet.
                                </p>
                              )}
                            </div>
                            <div className="flex flex-shrink-0 items-center gap-xs">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setPendingMoveSite({
                                    siteId: site.id,
                                    fromRegionId: region.id,
                                  })
                                }
                              >
                                Move Site
                              </Button>
                              <Button
                                variant="default"
                                size="sm"
                                rightIcon={<ChevronRight size={14} />}
                                onClick={() => onNavigateToSite?.(site.id)}
                              >
                                Configure
                              </Button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>

      {/* ---------- Create Region Dialog ---------- */}
      <Dialog
        open={createRegionOpen}
        onOpenChange={(open) => {
          if (!open) {
            setCreateRegionOpen(false);
            setNewRegionName("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create new Region</DialogTitle>
            <DialogDescription>
              Group Sites under a Region for hierarchical reporting and
              configuration.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-xxs mt-md">
            <Label>Region name</Label>
            <InputField
              value={newRegionName}
              onChange={(e) => setNewRegionName(e.target.value)}
              placeholder="e.g. KwaZulu-Natal"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCreateRegionOpen(false);
                setNewRegionName("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              disabled={!newRegionName.trim()}
              onClick={confirmCreateRegion}
            >
              Create Region
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Create Site Dialog ---------- */}
      <Dialog
        open={createSiteOpen}
        onOpenChange={(open) => {
          if (!open) closeCreateSite();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create new Site</DialogTitle>
            <DialogDescription>
              Pick the Region this Site belongs to, then name it. You'll be
              able to configure settings, geofence, and Rosters from the Site
              page after creating it.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-md space-y-md">
            <div className="space-y-xxs">
              <Label>Region</Label>
              <Select
                value={newSiteRegionId}
                onValueChange={setNewSiteRegionId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a Region" />
                </SelectTrigger>
                <SelectContent>
                  {regions.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-xxs">
              <Label>Site name</Label>
              <InputField
                value={newSiteName}
                onChange={(e) => setNewSiteName(e.target.value)}
                placeholder="e.g. Pretoria Office"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeCreateSite}>
              Cancel
            </Button>
            <Button
              variant="default"
              disabled={!newSiteRegionId || !newSiteName.trim()}
              onClick={confirmCreateSite}
            >
              Create Site
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Move Site Dialog ---------- */}
      <Dialog
        open={pendingMoveSite !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingMoveSite(null);
            setMoveSiteDestination("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move Site to another Region</DialogTitle>
            <DialogDescription>
              {pendingMoveSite && (
                <>
                  Move{" "}
                  <strong>
                    {regions
                      .find((r) => r.id === pendingMoveSite.fromRegionId)
                      ?.sites.find((s) => s.id === pendingMoveSite.siteId)
                      ?.name}
                  </strong>{" "}
                  to another Region. All Rosters and Site settings travel with
                  the Site.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-md space-y-xxs">
            <Label>Destination Region</Label>
            <Select
              value={moveSiteDestination}
              onValueChange={setMoveSiteDestination}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a Region" />
              </SelectTrigger>
              <SelectContent>
                {regions
                  .filter((r) => r.id !== pendingMoveSite?.fromRegionId)
                  .map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPendingMoveSite(null);
                setMoveSiteDestination("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              disabled={!moveSiteDestination}
              onClick={confirmMoveSite}
            >
              Move Site
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Move Roster Dialog ---------- */}
      <Dialog
        open={pendingMoveRoster !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingMoveRoster(null);
            setMoveRosterDestination("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move Roster to another Site</DialogTitle>
            <DialogDescription>
              {pendingMoveRoster && (
                <>
                  Move{" "}
                  <strong>
                    {
                      regions
                        .flatMap((r) => r.sites)
                        .find((s) => s.id === pendingMoveRoster.fromSiteId)
                        ?.rosters.find(
                          (rr) => rr.id === pendingMoveRoster.rosterId
                        )?.name
                    }
                  </strong>{" "}
                  to a different Site. The Roster starts fresh at the
                  destination — all settings reset to the destination Site's
                  defaults, and any custom values are cleared.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-md space-y-xxs">
            <Label>Destination Site</Label>
            <Select
              value={moveRosterDestination}
              onValueChange={setMoveRosterDestination}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a Site" />
              </SelectTrigger>
              <SelectContent>
                {allSites
                  .filter((s) => s.id !== pendingMoveRoster?.fromSiteId)
                  .map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPendingMoveRoster(null);
                setMoveRosterDestination("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              disabled={!moveRosterDestination}
              onClick={confirmMoveRoster}
            >
              Move Roster
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
